import { fetchApi } from '@/lib/api'

export type StoreNetworkStore = {
  global_store_id: string
  display_name: string
  canonical_url: string
  logo_url?: string | null
}

export type StoreNetworkCategoryResponse = {
  origin_global_store_id: string
  stores: Array<{
    store: StoreNetworkStore
    category_model: 'grupos' | 'classes'
    categories: Array<{
      id: number
      name: string
      order: number
      departments: Array<{
        id: number
        name: string
        product_groups: Array<{ id: number; name: string }>
      }>
    }>
    classes: Array<{ id: number; name: string; parent_id: number }>
  }>
  unavailable_stores: number
}

export type StoreNetworkSearchItem = {
  target_global_store_id: string
  target_product_id: number
  store: StoreNetworkStore
  store_logo_url: string | null
  name: string
  reference: string
  brand: string
  image_url: string | null
  in_stock: boolean
  price_status: 'calculating' | 'ready' | 'failed' | 'login_required' | 'unavailable'
  price: number | null
}

export type StoreNetworkSearchResponse = {
  items: StoreNetworkSearchItem[]
  total: number
  has_more: boolean
  unavailable_stores: number
  price_batch_token: string | null
}

const withStore = (path: string, lojaId: number, params: Record<string, string | number | null> = {}) => {
  const query = new URLSearchParams({ loja_id: String(lojaId) })
  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== '') query.set(key, String(value))
  })
  return `${path}?${query}`
}

export const getStoreNetworkCategories = (lojaId: number, signal?: AbortSignal) =>
  fetchApi<StoreNetworkCategoryResponse>(withStore('/site/rede-lojas/categorias', lojaId), { signal, cache: 'no-store' })

export const searchStoreNetwork = (lojaId: number, input: {
  query: string
  targetGlobalStoreId?: string | null
  categoryId?: number | null
  departmentId?: number | null
  productGroupId?: number | null
  classId?: number | null
  page?: number
  pageSize?: number
}, signal?: AbortSignal) => fetchApi<StoreNetworkSearchResponse>(withStore('/site/rede-lojas/busca', lojaId, {
  q: input.query,
  target_global_store_id: input.targetGlobalStoreId ?? null,
  category_id: input.categoryId ?? null,
  department_id: input.departmentId ?? null,
  product_group_id: input.productGroupId ?? null,
  class_id: input.classId ?? null,
  page: input.page ?? 1,
  page_size: input.pageSize ?? 24,
}), { signal, cache: 'no-store' })

export const getStoreNetworkPrices = (batchToken: string) =>
  fetchApi<{ batches: Array<{
    status: 'calculating' | 'ready' | 'failed'
    target_global_store_id?: string
    prices?: Array<{ product_id: number; price: number | null }>
  }> }>(`/site/rede-lojas/precos/${encodeURIComponent(batchToken)}`, { cache: 'no-store' })

export const getStoreNetworkHandoffSession = () =>
  fetchApi<{ entidade_id: number; buyer_session_token: string }>('/site/rede-lojas/sessao', {
    cache: 'no-store',
    skipBuyerSessionToken: true,
    suppressBuyerSessionExpiry: true,
  })

export const createStoreNetworkHandoff = (lojaId: number, input: {
  targetGlobalStoreId: string
  targetProductId: number
  journeyId: string
  analyticsVisitorId?: string
  analyticsSessionId?: string
  analyticsDeviceType?: 'desktop' | 'mobile' | 'other'
}) => fetchApi<{ ticket: string; action_url: string; journey_id: string }>('/site/rede-lojas/handoff', {
  method: 'POST',
  headers: { 'content-type': 'application/json' },
  body: JSON.stringify({
    loja_id: lojaId,
    target_global_store_id: input.targetGlobalStoreId,
    target_product_id: input.targetProductId,
    journey_id: input.journeyId,
    analytics_visitor_id: input.analyticsVisitorId,
    analytics_session_id: input.analyticsSessionId,
    analytics_device_type: input.analyticsDeviceType,
  }),
  cache: 'no-store',
})
